// src/components/navbar/NavbarLinksAdmin.js
// Implementation using centralized Auth Utils for User Info display and Logout functionality
//
// --- UPDATE (toast once) ---------------------------------------------------------
//   • The second notification bell is gone — AdminNavbar shows the one FlowSense Notify bell.
//   • Logout also turns off browser push for THIS browser (the next person on this computer must
//     not get the previous user's pop-ups); the live stream stops by itself once signed out.
//   • Unused imports removed.
// --- END UPDATE ------------------------------------------------------------------

// Chakra Imports
import {
  Avatar, Button, Flex, Icon, Link, Menu, MenuButton, MenuItem,
  MenuList, Text, useColorModeValue, useColorMode, useToast, Spinner, Tag, HStack, Divider, VStack
} from '@chakra-ui/react';
// Custom Components
import { SearchBar } from './searchBar/SearchBar';   // Ensure path is correct
import { SidebarResponsive } from '../sidebar/SidebarResponsive'; // Ensure path is correct
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Assets
import { MdInfoOutline } from 'react-icons/md';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import useSystemHealth from '../../hooks/useSystemHealth';
import * as notifyWebPush from '../../utils/notifyWebPush'; // --- UPDATE
import { FaDatabase, FaServer, FaBrain } from 'react-icons/fa';
import routes from '../../routes'; // Ensure path is correct

// --- Import Auth Utils ---
import { getUserInfo, clearAuthData } from '../../utils/auth'; // Import helpers from centralized utils

export default function AdminNavbarLinks(props) {
  const { secondary } = props; 
  const { colorMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();
  const toast = useToast();

  // State to hold the logged-in user's name
  const [userName, setUserName] = useState("User"); // Default name
  const { health, isLoading: isHealthLoading, error: healthError, lastChecked } = useSystemHealth(); // --- NEW: Use the hook

  // Get user info on component mount using utility function
  useEffect(() => {
    const userInfo = getUserInfo(); // Use imported helper (reads from sessionStorage)
    if (userInfo && userInfo.username) {
      setUserName(userInfo.username);
    }
  }, []); 

  // --- Logout Handler ---
  const handleLogout = async () => {
    // --- UPDATE: stop browser push for this browser while the token is still valid (max 2 s)
    if (notifyWebPush.isEnabledHere()) {
      await Promise.race([notifyWebPush.disable().catch(() => {}), new Promise(r => setTimeout(r, 2000))]);
    }
    // --- END UPDATE
    clearAuthData(); // Use imported helper to clear sessionStorage

    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    navigate('/auth/sign-in'); // Redirect to sign-in
  };
  // --- End Logout Handler ---

  // Chakra Color Mode values
  const navbarIcon = useColorModeValue('gray.400', 'white');
  let menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const shadow = useColorModeValue('14px 17px 40px 4px rgba(112, 144, 176, 0.18)', '14px 17px 40px 4px rgba(112, 144, 176, 0.06)');
  const borderButton = useColorModeValue('secondaryGray.500', 'whiteAlpha.200');

  const grayColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Flex
      w={{ sm: '100%', md: 'auto' }} alignItems="center" flexDirection="row"
      bg={menuBg} flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'nowrap'}
      p="10px" borderRadius="30px" boxShadow={shadow}
    >
      {/* Search Bar */}
      <SearchBar mb={secondary ? { base: '10px', md: 'unset' } : 'unset'} me="10px" borderRadius="30px" />

      {/* Responsive Sidebar Toggle */}
      {/* Passes all props down, including isOpen, onOpen, onClose needed by SidebarResponsive */}
      <SidebarResponsive routes={routes} {...props} />

      {/* --- UPDATE: notifications bell lives in AdminNavbar (one bell, FlowSense Notify) */}

      {/* Info Menu */}
      <Menu>
        <MenuButton p="0px" aria-label="Information menu">
          <Icon mt="6px" as={MdInfoOutline} color={navbarIcon} w="18px" h="18px" me="10px" />
        </MenuButton>
        <MenuList boxShadow={shadow} p="15px" borderRadius="20px" bg={menuBg} border="none" mt="22px" minW="300px">
          <Text fontSize="md" fontWeight="600" color={textColor} mb="10px">System Status</Text>
          <VStack align="stretch" spacing={3}>
            {isHealthLoading ? (
              <Spinner />
            ) : healthError ? (
              <Text color="red.500" fontSize="sm">{healthError}</Text>
            ) : (
              <>
                <HStack justify="space-between">
                  <HStack><Icon as={FaServer} /> <Text fontSize="sm">Backend API</Text></HStack>
                  <Tag colorScheme={health.backend === 'Online' ? 'green' : 'red'}>
                    {health.backend}
                  </Tag>
                </HStack>
                <HStack justify="space-between">
                  <HStack><Icon as={FaDatabase} /> <Text fontSize="sm">Database</Text></HStack>
                  <Tag colorScheme={health.database === 'Connected' ? 'green' : 'red'}>
                    {health.database}
                  </Tag>
                </HStack>
                <HStack justify="space-between">
                  <HStack><Icon as={FaBrain} /> <Text fontSize="sm">ML Service</Text></HStack>
                  <Tag colorScheme={health.mlService === 'Online' ? 'green' : 'red'}>
                    {health.mlService}
                  </Tag>
                </HStack>
              </>
            )}
          </VStack>
          <Text fontSize="xs" color={grayColor} mt="10px">
            Last checked: {lastChecked ? lastChecked.toLocaleTimeString() : 'N/A'}
          </Text>
          <Divider my={3} />
          <MenuItem as={Link} href="/docs" isExternal _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius="8px" px="5px">
            <Text fontSize="sm">Help & Documentation</Text>
          </MenuItem>
          <MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius="8px" px="5px">
            <Text fontSize="sm">What's New</Text>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Theme Toggle Button */}
      <Button variant="no-hover" bg="transparent" p="0px" minW="unset" minH="unset" h="18px" w="max-content" onClick={toggleColorMode} aria-label="Toggle color mode">
        <Icon me="10px" h="18px" w="18px" color={navbarIcon} as={colorMode === 'light' ? IoMdMoon : IoMdSunny} />
      </Button>

      {/* Profile Menu */}
      <Menu>
        <MenuButton p="0px" aria-label="User menu">
          <Avatar _hover={{ cursor: 'pointer' }} color="white" name={userName} bg="#11047A" size="sm" w="40px" h="40px" />
        </MenuButton>
        <MenuList boxShadow={shadow} p="0px" mt="10px" borderRadius="20px" bg={menuBg} border="none">
          <Flex w="100%" mb="0px">
            <Text ps="20px" pt="16px" pb="10px" w="100%" borderBottom="1px solid" borderColor={borderColor} fontSize="sm" fontWeight="700" color={textColor}> 👋&nbsp; Hey, {userName} </Text>
          </Flex>
          <Flex flexDirection="column" p="10px">
            <MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius="8px" px="14px"> <Text fontSize="sm">Profile Settings</Text> </MenuItem>
            <MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius="8px" px="14px"> <Text fontSize="sm">Newsletter Settings</Text> </MenuItem>
            <MenuItem _hover={{ bg: 'red.100' }} _focus={{ bg: 'red.100' }} color="red.500" borderRadius="8px" px="14px" onClick={handleLogout}>
              <Text fontSize="sm" fontWeight="bold">Log out</Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
    </Flex>
  );
}

AdminNavbarLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  logoText: PropTypes.string,
  scrolled: PropTypes.bool,
};